import {
  createContext,
  lazy,
  ReactNode,
  Suspense,
  useContext,
  useCallback,
  useState,
  useEffect,
  useRef,
} from 'react';
import { useHistory } from 'react-router-dom';
import { googleRequest, errorMessage } from './api';
import { draftResponseSchema, SavedDraft } from './mailDraft';
import type { MailDraftRequest } from './MailComposer';

const Composer = lazy(() =>
  import('./MailComposer').then((module) => ({ default: module.Composer }))
);
const DraftContext = createContext<((request: MailDraftRequest) => void) | undefined>(undefined);
export const useMailComposer = () => useContext(DraftContext);

// Lives above routes: page changes must not destroy an email or its exact send command.
export function MailComposerProvider({ uid, children }: { uid?: string; children: ReactNode }) {
  return (
    <DraftSession key={uid || 'signed-out'} uid={uid}>
      {children}
    </DraftSession>
  );
}
function DraftSession({ uid, children }: { uid?: string; children: ReactNode }) {
  const history = useHistory();
  const [draft, setDraft] = useState<{
    request: MailDraftRequest;
    activation: number;
    revision: string | null;
    restored?: SavedDraft;
  }>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const alive = useRef(true);
  const loadingRef = useRef(false);
  const requested = useRef<MailDraftRequest>();
  const load = useCallback(
    async (request?: MailDraftRequest) => {
      if (request) requested.current = request;
      if (!uid || loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);
      setError('');
      try {
        const saved = await googleRequest({ action: 'mailDraft' }, draftResponseSchema);
        if (!alive.current) return;
        if (saved.draft || requested.current)
          setDraft({
            request: saved.draft ? { uid, ...saved.draft.request } : requested.current!,
            activation: 0,
            revision: saved.revision,
            restored: saved.draft || undefined,
          });
      } catch (cause) {
        if (alive.current && requested.current) setError(errorMessage(cause));
      } finally {
        requested.current = undefined;
        loadingRef.current = false;
        if (alive.current) setLoading(false);
      }
    },
    [uid]
  );
  useEffect(() => {
    alive.current = true;
    void load();
    return () => {
      alive.current = false;
    };
  }, [load]);
  return (
    <DraftContext.Provider
      value={(request) => {
        if (!uid || request.uid !== uid) return;
        // Keep the existing draft, including attachments and an uncertain send, intact.
        if (draft) setDraft({ ...draft, activation: draft.activation + 1 });
        else void load(request);
      }}
    >
      {children}
      {loading && (
        <p
          role="status"
          className="fixed bottom-0 right-4 z-50 rounded-t-lg border bg-background p-3 text-sm shadow"
        >
          Henter din private kladde…
        </p>
      )}
      {error && (
        <p
          role="alert"
          className="fixed bottom-0 right-4 z-50 max-w-sm rounded-t-lg border bg-background p-3 text-sm shadow"
        >
          Kunne ikke hente kladden. {error}
        </p>
      )}
      {draft && (
        <Suspense
          fallback={
            <p
              role="status"
              className="fixed bottom-0 right-4 z-50 rounded-t-lg border bg-background p-4 shadow-xl"
            >
              Åbner email…
            </p>
          }
        >
          <Composer
            {...draft.request}
            activation={draft.activation}
            restored={draft.restored}
            revision={draft.revision}
            onClose={() => setDraft(undefined)}
            onSent={(threadId) => {
              history.push('/crm?kind=deal&view=mail', { crmMailThread: threadId });
            }}
          />
        </Suspense>
      )}
    </DraftContext.Provider>
  );
}
